import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { HeroVisual } from '../components/HeroVisual';
import { ButtonLink } from '../components/ButtonLink';
import { Reveal } from '../components/Reveal';
import { MethodSteps } from '../components/MethodSteps';
import { CtaBand } from '../components/CtaBand';
import { practiceAreas } from '../data/expertise';
import { industries } from '../data/industries';
import { insights } from '../data/insights';
import { formatDate } from '../utils/date';
export function Home() {
  const [featured, ...others] = insights;
  return <>
      <section className="relative overflow-hidden bg-midnight-950 text-champagne-50" data-id="element-283" data-mp="29etv6ot45n6k">
        <div className="mx-auto grid max-w-site items-center gap-10 px-6 pb-20 pt-14 lg:grid-cols-12 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-284" data-mp="2dj9z4kj62kj5">
          <div className="lg:col-span-7" data-id="element-285" data-mp="3kxmx98ldf2ct">
            <h1 className="font-serif text-5xl leading-[1.02] md:text-6xl lg:text-7xl" data-id="element-286" data-mp="3ovuc1xw9xtld">Leadership that defines what comes next.</h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-champagne-100/75" data-id="element-287" data-mp="30w04g0o65k41">
              Qorvessa Executive Partners is a retained executive search firm placing board members, chief executives, and senior
              leaders with organizations at moments of consequence.
            </p>
            <div className="mt-10 flex flex-wrap gap-4" data-id="element-288" data-mp="2mwqmavgekfxf">
              <ButtonLink to="/contact?type=employer" withArrow data-id="element-289" data-mp="2hy6zytxwd24j">
                Discuss Your Hiring Needs
              </ButtonLink>
              <ButtonLink to="/contact?type=candidate" variant="outlineDark" data-id="element-290" data-mp="2d5w1lvit1oe3">
                Introduce Yourself
              </ButtonLink>
            </div>
          </div>
          <div className="lg:col-span-5" data-id="element-291" data-mp="23a2fplj74ijn">
            <HeroVisual className="mx-auto aspect-square w-full max-w-[520px]" data-id="element-292" data-mp="236y8efafp6or" />
          </div>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-293" data-mp="1u8l52iok592c">
        <div className="mx-auto grid max-w-site gap-10 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32" data-id="element-294" data-mp="1u9ns2firercz">
          <Reveal className="lg:col-span-7" data-id="element-295" data-mp="1kjxliswfqf37">
            <h2 className="font-serif text-4xl leading-tight text-midnight-900 md:text-5xl" data-id="element-296" data-mp="1omc8np75010a">
              We search for the leaders others overlook — and recommend only those we would stand behind.
            </h2>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-4 lg:col-start-9 lg:pt-3" data-id="element-297" data-mp="1fmp4ivn0wh3j">
            <p className="text-[17px] leading-relaxed text-midnight-900/70" data-id="element-298" data-mp="23aouvugfa5hl">
              Every mandate is partner-led, research-driven, and handled with complete discretion. We work with a deliberately
              small number of clients so each search receives the senior attention it deserves.
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-299" data-mp="1yjgjwtzg2c2b">
              About the firm
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-300" data-mp="2r1uiwwwio42z" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-champagne-50" data-id="element-301" data-mp="fjpozvovpv5e">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-302" data-mp="an8yiuu7j4p5">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-303" data-mp="oe4c2kfni9z2">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-304" data-mp="ofyrjj85l9vr">Our expertise</h2>
            <Link to="/expertise" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-305" data-mp="kewossf12fp1">
              All practice areas
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-306" data-mp="5nmp6whi28lm" />
            </Link>
          </div>
          <ul className="mt-12 border-b border-midnight-900/15" data-id="element-307" data-mp="ot8ywatw3hr">
            {practiceAreas.map(area => <li key={area.id} className="border-t border-midnight-900/15" data-id="element-308" data-mp="1h29yrcq9m91j">
                <Link to={`/expertise#${area.id}`} className="group grid items-baseline gap-3 py-7 md:grid-cols-12 md:gap-8" data-id="element-309" data-mp="1c3qcfb7rev8n">
                  <h3 className="font-serif text-2xl text-midnight-900 transition-colors duration-150 ease-out group-hover:text-midnight-600 md:col-span-4 md:text-3xl" data-id="element-310" data-mp="27eet4jjm5hm5">
                    {area.title}
                  </h3>
                  <p className="text-[15px] leading-relaxed text-midnight-900/70 md:col-span-7" data-id="element-311" data-mp="2hb9o4sgogzvo">{area.summary}</p>
                  <ArrowUpRightIcon className="hidden h-5 w-5 justify-self-end text-champagne-600 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 md:col-span-1 md:block" aria-hidden="true" data-id="element-312" data-mp="2mcgv4pazxv1a" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-midnight-950 text-champagne-50" data-id="element-313" data-mp="351dqkwi4qtl3">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-314" data-mp="350vjt5xucwyx">
          <div className="grid gap-8 lg:grid-cols-12" data-id="element-315" data-mp="3a21evaez4wbm">
            <h2 className="font-serif text-4xl md:text-5xl lg:col-span-5" data-id="element-316" data-mp="3e2bn6tq8nytn">Industries we serve</h2>
            <p className="text-[17px] leading-relaxed text-champagne-100/70 lg:col-span-5 lg:col-start-8" data-id="element-317" data-mp="12f00mfjmt92s">
              Deep sector networks paired with cross-industry perspective — so we can bring you leaders who know your market, and
              those who can transform it.
            </p>
          </div>
          <ul className="mt-14 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-318" data-mp="ofgvlvm4adup">
            {industries.map(industry => <li key={industry.id} className="border-t border-champagne-100/15 py-6" data-id="element-319" data-mp="1m1xmoj49bv80">
                <Link to={`/industries#${industry.id}`} className="font-serif text-2xl text-champagne-50 transition-colors duration-150 ease-out hover:text-champagne-300" data-id="element-320" data-mp="1h3md89hvjmqb">
                  {industry.name}
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-321" data-mp="24tqfacfezef7">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-322" data-mp="24urtuqvn2j1o">
          <div className="max-w-2xl" data-id="element-323" data-mp="201yp43067apl">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-324" data-mp="200xd9wbbyk6t">A search methodology built on evidence</h2>
            <p className="mt-5 text-[17px] leading-relaxed text-midnight-900/70" data-id="element-325" data-mp="26jrjjzxqeqw">
              Six disciplined stages take every mandate from first conversation to a successful first year in role.
            </p>
          </div>
          <div className="mt-14" data-id="element-326" data-mp="3piu9nycnlqhb">
            <MethodSteps data-id="element-327" data-mp="3phridpbf5m0v" />
          </div>
        </div>
      </section>

      <section className="bg-ivory pb-24 lg:pb-32" data-id="element-328" data-mp="3altromk9l9w1">
        <div className="mx-auto grid max-w-site gap-6 px-6 lg:grid-cols-2 lg:px-10" data-id="element-329" data-mp="3floikq41hzh3">
          <Reveal className="flex h-full flex-col rounded-3xl bg-midnight-900 p-10 text-champagne-50 lg:p-14" data-id="element-330" data-mp="3fjbr9mce6crd">
            <p className="text-sm font-medium text-champagne-300" data-id="element-331" data-mp="36l05bxmh0359">For Employers</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight" data-id="element-332" data-mp="36muph9px3r8a">Build the leadership team your strategy requires.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-champagne-100/70" data-id="element-333" data-mp="14td8vgjt7g1e">
              Partner-led retained search for board, C-suite, and senior roles, with candid market intelligence at every stage.
            </p>
            <div className="mt-auto flex flex-wrap gap-4 pt-10" data-id="element-334" data-mp="19zjqtrk6jifw">
              <ButtonLink to="/contact?type=employer" withArrow data-id="element-335" data-mp="1npvkvw0brr9o">
                Discuss Your Hiring Needs
              </ButtonLink>
              <ButtonLink to="/employers" variant="outlineDark" data-id="element-336" data-mp="1iy43aaz5cvvo">
                How we partner
              </ButtonLink>
            </div>
          </Reveal>
          <Reveal delay={0.06} className="flex h-full flex-col rounded-3xl bg-champagne-100 p-10 text-midnight-900 lg:p-14" data-id="element-337" data-mp="1soyrhkvye5jr">
            <p className="text-sm font-medium text-champagne-600" data-id="element-338" data-mp="26p9g0h9sj81r">For Candidates</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight" data-id="element-339" data-mp="22nfl6pma0y9i">Explore your next leadership chapter, in confidence.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-midnight-900/70" data-id="element-340" data-mp="c9ymlh6ljuzs">
              Many of our mandates are never advertised. Introduce yourself and we’ll be in touch when the right opportunity arises.
            </p>
            <div className="mt-auto flex flex-wrap gap-4 pt-10" data-id="element-341" data-mp="wttbqvvxw2xf">
              <ButtonLink to="/contact?type=candidate" variant="solidDark" withArrow data-id="element-342" data-mp="11scyuzm9rkrd">
                Introduce Yourself
              </ButtonLink>
              <ButtonLink to="/opportunities" variant="outlineLight" data-id="element-343" data-mp="16lzcau09bso8">
                Leadership Opportunities
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-midnight-900/10 bg-champagne-50" data-id="element-344" data-mp="dxa7ajw2z377">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-345" data-mp="iutwufy8b2eg">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-346" data-mp="muucg3x2djn3">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-347" data-mp="rv8kvn2kuvln">Insights</h2>
            <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-348" data-mp="1ybkfje66xvch">
              All insights
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-349" data-mp="2dec0y62xn0sp" />
            </Link>
          </div>
          <div className="mt-12 grid gap-10 lg:grid-cols-12" data-id="element-350" data-mp="28epo3xr0nmkf">
            <Reveal className="lg:col-span-7" data-id="element-351" data-mp="23o9aacja9yxj">
              <Link to={`/insights/${featured.slug}`} className="group flex h-full flex-col rounded-3xl bg-midnight-900 p-10 text-champagne-50 lg:p-12" data-id="element-352" data-mp="23n7ope4n5u1k">
                <p className="text-sm text-champagne-300" data-id="element-353" data-mp="1unkx1k1ps2lp">
                  {featured.category} · {formatDate(featured.date)}
                </p>
                <h3 className="mt-6 font-serif text-4xl leading-tight transition-colors duration-150 ease-out group-hover:text-champagne-200" data-id="element-354" data-mp="1pnq5dea478zl">
                  {featured.title}
                </h3>
                <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-champagne-100/70" data-id="element-355" data-mp="1gp4p5ps4861z">{featured.excerpt}</p>
                <span className="mt-auto inline-flex items-center gap-2 pt-10 text-sm font-medium text-champagne-200" data-id="element-356" data-mp="3f2mu96ax1u8a">
                  Read article <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-357" data-mp="col6egebr5er" />
                </span>
              </Link>
            </Reveal>
            <ul className="lg:col-span-5" data-id="element-358" data-mp="mmiw78bu1a9h">
              {others.slice(0, 3).map(article => <li key={article.slug} className="border-t border-midnight-900/15" data-id="element-359" data-mp="hnxu13d0sgie">
                  <Link to={`/insights/${article.slug}`} className="group block py-6" data-id="element-360" data-mp="hlv2gew7yfbk">
                    <p className="text-sm text-midnight-900/55" data-id="element-361" data-mp="3q1es73xolflv">
                      {article.category} · {article.readTime}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl leading-snug text-midnight-900 transition-colors duration-150 ease-out group-hover:text-midnight-600" data-id="element-362" data-mp="3oyhgu2s7s2l">
                      {article.title}
                    </h3>
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <CtaBand data-id="element-363" data-mp="32bswwrkfjkj5" />
    </>;
}